import axiosInstance from './axiosInstance';
import { API_BASE_URL } from '../config';

type PreviewType = 'thumbnail' | 'medium';

let signedPreviewsEnabled: boolean | null = null;
let flagsInFlight: Promise<void> | null = null;
const tokenCache: Map<string, { token: string; expiresAt: number }> = new Map();
let io: IntersectionObserver | null = null;

// LocalStorage persistence for preview tokens (survives F5)
const LS_KEY = 'dam_preview_tokens_v1';
type PersistedMap = Record<string, { token: string; expiresAt: number }>;

function readPersisted(): PersistedMap {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return typeof obj === 'object' && obj ? obj as PersistedMap : {};
  } catch { return {}; }
}

function writePersisted(map: PersistedMap): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch {}
}

function getPersisted(key: string, now = Date.now()): { token: string; expiresAt: number } | null {
  try {
    const map = readPersisted();
    const v = map[key];
    if (!v) return null;
    if (v.expiresAt <= now) {
      delete map[key];
      writePersisted(map);
      return null;
    }
    return v;
  } catch { return null; }
}

function setPersisted(key: string, value: { token: string; expiresAt: number }): void {
  try {
    const map = readPersisted();
    map[key] = value;
    writePersisted(map);
  } catch {}
}

async function fetchFlags(): Promise<void> {
  // Force unsigned previews to allow stable URLs and maximal browser cache hit-rate
  if (signedPreviewsEnabled !== null) return;
  signedPreviewsEnabled = false;
}

export async function getPreviewUrl(assetId: string, type: PreviewType, size?: number): Promise<string> {
  await fetchFlags();
  const useSigned = !!signedPreviewsEnabled;
  if (!useSigned) {
    // Backward compatible: include JWT in query if available
    const jwt = (typeof window !== 'undefined') ? localStorage.getItem('authToken') : null;
    const q = jwt ? `?token=${encodeURIComponent(jwt)}` : '';
    const s = size && size > 0 ? `${q ? '&' : '?'}size=${size}` : '';
    const f = `${q || s ? '&' : '?'}format=webp`;
    return `${API_BASE_URL}/api/assets/${assetId}/preview/${type}${q}${s}${f}`;
  }

  const key = `${assetId}:${type}:${size || 0}`;
  const now = Date.now();
  // In-memory first
  const cached = tokenCache.get(key) || getPersisted(key, now);
  if (cached && cached.expiresAt > now) {
    const s = size && size > 0 ? `&size=${size}` : '';
    return `${API_BASE_URL}/api/assets/${assetId}/preview/${type}?token=${encodeURIComponent(cached.token)}${s}&format=webp`;
  }

  // Request a fresh token and cache with a safety margin
  const { data } = await axiosInstance.post('/api/assets/preview-token', { assetId, type });
  const ttlSec: number = Number(data?.ttl || 0) || 900;
  const token: string = data?.token;
  const expiresAt = now + ttlSec * 1000 - 5000; // 5s margin
  tokenCache.set(key, { token, expiresAt });
  setPersisted(key, { token, expiresAt });
  const s = size && size > 0 ? `&size=${size}` : '';
  return `${API_BASE_URL}/api/assets/${assetId}/preview/${type}?token=${encodeURIComponent(token)}${s}&format=webp`;
}

function getObserver(): IntersectionObserver {
  if (io) return io;
  io = new IntersectionObserver((entries) => {
    entries.forEach(async (entry) => {
      if (!entry.isIntersecting) return;
      const img = entry.target as HTMLImageElement;
      const assetId = img.getAttribute('data-asset-id');
      const type = (img.getAttribute('data-preview-type') as PreviewType) || 'thumbnail';
      const sizeAttr = img.getAttribute('data-preview-size');
      const size = sizeAttr ? parseInt(sizeAttr, 10) : undefined;
      if (!assetId) return;
      try {
        const url = await getPreviewUrl(assetId, type, size);
        // Only replace if element still mounted
        img.src = url;
      } catch {}
      io && io.unobserve(img);
    });
  }, { rootMargin: '800px 0px', threshold: 0 });
  return io;
}

export function observePreview(imgEl: HTMLImageElement, assetId: string, type: PreviewType, size?: number): void {
  // Attach attributes; observer will fetch only when near/in viewport
  try {
    imgEl.setAttribute('data-asset-id', assetId);
    imgEl.setAttribute('data-preview-type', type);
    if (size && size > 0) imgEl.setAttribute('data-preview-size', String(size));
    const observer = getObserver();
    observer.observe(imgEl);
  } catch {}
}


